import { lazy } from "react";

/**
 * Creates a lazy-loaded component with retry logic
 * @param {Function} importFunc - The dynamic import function
 * @param {number} retries - Number of retry attempts (default: 3)
 * @returns {React.LazyExoticComponent} Lazy component
 */
export const lazyLoad = (importFunc, retries = 3) => {
  return lazy(() => {
    return new Promise((resolve, reject) => {
      const attempt = (attemptsLeft) => {
        importFunc()
          .then(resolve)
          .catch((error) => {
            if (attemptsLeft === 1) {
              reject(error);
            } else {
              // Retry after a short delay
              setTimeout(() => attempt(attemptsLeft - 1), 1000);
            }
          });
      };
      attempt(retries);
    });
  });
};

/**
 * Preload a component for better user experience
 * @param {Function} importFunc - The dynamic import function
 */
export const preloadComponent = (importFunc) => {
  importFunc();
};

/**
 * Lazy load with custom chunk name for better caching
 * @param {Function} importFunc - The dynamic import function
 * @param {string} chunkName - Custom chunk name
 * @returns {React.LazyExoticComponent} Lazy component
 */
export const lazyLoadWithChunk = (importFunc, chunkName) => {
  return lazy(() => {
    return importFunc().then((module) => {
      // Add chunk name to module for better debugging
      if (module.default) {
        module.default.displayName = chunkName;
      }
      return module;
    });
  });
};
